import React, { createContext, useContext, useMemo } from 'react';
import { STAT_SCOPE_OPTIONS } from '../constants/statScopes';
import { CANADIAN_PROVINCES, MEXICAN_STATES } from '../constants/states';

const DEFAULT_API_BASE =
	process.env.NODE_ENV === 'production' ? 'https://api.platechase.com' : 'http://localhost:8787';

/** Hardcoded fallback when GET /jurisdictions has not loaded yet. */
const FALLBACK_SCOPES = [
	{
		code: 'US',
		label: 'United States',
		flag: '🇺🇸',
		regionsLabel: 'US regions',
		mapKind: 'topo_us',
		geoUrl: 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json',
		projection: { projection: 'geoAlbersUsa' },
		displayOrder: 10,
		isCombined: false,
	},
	{
		code: 'CAN',
		label: 'Canada',
		flag: '🇨🇦',
		regionsLabel: 'Canadian provinces',
		mapKind: 'geojson_url',
		geoUrl:
			'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson',
		projection: {
			projection: 'geoConicEqualArea',
			rotate: [96, 0, 0],
			scale: 800,
			center: [0, 64],
			parallels: [49, 80],
			clipExtent: [
				[0, 0],
				[100, 500],
			],
		},
		displayOrder: 20,
		isCombined: false,
	},
	{
		code: 'MX',
		label: 'Mexico',
		flag: '🇲🇽',
		regionsLabel: 'Mexican states',
		mapKind: 'geojson_url',
		geoUrl:
			'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/mexico.geojson',
		projection: {
			projection: 'geoConicEqualArea',
			rotate: [102, 0, 0],
			scale: 1500,
			center: [0, 24],
			parallels: [17, 32],
		},
		displayOrder: 30,
		isCombined: false,
	},
	{
		code: 'COMBINED',
		label: 'North America',
		flag: '🌎',
		regionsLabel: 'North American regions',
		mapKind: 'composite',
		geoUrl: 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json',
		projection: { projection: 'geoAlbersUsa' },
		displayOrder: 40,
		isCombined: true,
	},
];

const FALLBACK_JURISDICTIONS = [
	{ id: '01', scopeCode: 'US', name: 'Alabama', geoMatchName: 'Alabama', displayOrder: 1 },
	{ id: '02', scopeCode: 'US', name: 'Alaska', geoMatchName: 'Alaska', displayOrder: 2 },
	{ id: '04', scopeCode: 'US', name: 'Arizona', geoMatchName: 'Arizona', displayOrder: 3 },
	{ id: '05', scopeCode: 'US', name: 'Arkansas', geoMatchName: 'Arkansas', displayOrder: 4 },
	{ id: '06', scopeCode: 'US', name: 'California', geoMatchName: 'California', displayOrder: 5 },
	{ id: '08', scopeCode: 'US', name: 'Colorado', geoMatchName: 'Colorado', displayOrder: 6 },
	{ id: '09', scopeCode: 'US', name: 'Connecticut', geoMatchName: 'Connecticut', displayOrder: 7 },
	{ id: '10', scopeCode: 'US', name: 'Delaware', geoMatchName: 'Delaware', displayOrder: 8 },
	{ id: '11', scopeCode: 'US', name: 'District of Columbia', geoMatchName: 'District of Columbia', displayOrder: 9 },
	{ id: '12', scopeCode: 'US', name: 'Florida', geoMatchName: 'Florida', displayOrder: 10 },
	{ id: '13', scopeCode: 'US', name: 'Georgia', geoMatchName: 'Georgia', displayOrder: 11 },
	{ id: '15', scopeCode: 'US', name: 'Hawaii', geoMatchName: 'Hawaii', displayOrder: 12 },
	{ id: '16', scopeCode: 'US', name: 'Idaho', geoMatchName: 'Idaho', displayOrder: 13 },
	{ id: '17', scopeCode: 'US', name: 'Illinois', geoMatchName: 'Illinois', displayOrder: 14 },
	{ id: '18', scopeCode: 'US', name: 'Indiana', geoMatchName: 'Indiana', displayOrder: 15 },
	{ id: '19', scopeCode: 'US', name: 'Iowa', geoMatchName: 'Iowa', displayOrder: 16 },
	{ id: '20', scopeCode: 'US', name: 'Kansas', geoMatchName: 'Kansas', displayOrder: 17 },
	{ id: '21', scopeCode: 'US', name: 'Kentucky', geoMatchName: 'Kentucky', displayOrder: 18 },
	{ id: '22', scopeCode: 'US', name: 'Louisiana', geoMatchName: 'Louisiana', displayOrder: 19 },
	{ id: '23', scopeCode: 'US', name: 'Maine', geoMatchName: 'Maine', displayOrder: 20 },
	{ id: '24', scopeCode: 'US', name: 'Maryland', geoMatchName: 'Maryland', displayOrder: 21 },
	{ id: '25', scopeCode: 'US', name: 'Massachusetts', geoMatchName: 'Massachusetts', displayOrder: 22 },
	{ id: '26', scopeCode: 'US', name: 'Michigan', geoMatchName: 'Michigan', displayOrder: 23 },
	{ id: '27', scopeCode: 'US', name: 'Minnesota', geoMatchName: 'Minnesota', displayOrder: 24 },
	{ id: '28', scopeCode: 'US', name: 'Mississippi', geoMatchName: 'Mississippi', displayOrder: 25 },
	{ id: '29', scopeCode: 'US', name: 'Missouri', geoMatchName: 'Missouri', displayOrder: 26 },
	{ id: '30', scopeCode: 'US', name: 'Montana', geoMatchName: 'Montana', displayOrder: 27 },
	{ id: '31', scopeCode: 'US', name: 'Nebraska', geoMatchName: 'Nebraska', displayOrder: 28 },
	{ id: '32', scopeCode: 'US', name: 'Nevada', geoMatchName: 'Nevada', displayOrder: 29 },
	{ id: '33', scopeCode: 'US', name: 'New Hampshire', geoMatchName: 'New Hampshire', displayOrder: 30 },
	{ id: '34', scopeCode: 'US', name: 'New Jersey', geoMatchName: 'New Jersey', displayOrder: 31 },
	{ id: '35', scopeCode: 'US', name: 'New Mexico', geoMatchName: 'New Mexico', displayOrder: 32 },
	{ id: '36', scopeCode: 'US', name: 'New York', geoMatchName: 'New York', displayOrder: 33 },
	{ id: '37', scopeCode: 'US', name: 'North Carolina', geoMatchName: 'North Carolina', displayOrder: 34 },
	{ id: '38', scopeCode: 'US', name: 'North Dakota', geoMatchName: 'North Dakota', displayOrder: 35 },
	{ id: '39', scopeCode: 'US', name: 'Ohio', geoMatchName: 'Ohio', displayOrder: 36 },
	{ id: '40', scopeCode: 'US', name: 'Oklahoma', geoMatchName: 'Oklahoma', displayOrder: 37 },
	{ id: '41', scopeCode: 'US', name: 'Oregon', geoMatchName: 'Oregon', displayOrder: 38 },
	{ id: '42', scopeCode: 'US', name: 'Pennsylvania', geoMatchName: 'Pennsylvania', displayOrder: 39 },
	{ id: '44', scopeCode: 'US', name: 'Rhode Island', geoMatchName: 'Rhode Island', displayOrder: 40 },
	{ id: '45', scopeCode: 'US', name: 'South Carolina', geoMatchName: 'South Carolina', displayOrder: 41 },
	{ id: '46', scopeCode: 'US', name: 'South Dakota', geoMatchName: 'South Dakota', displayOrder: 42 },
	{ id: '47', scopeCode: 'US', name: 'Tennessee', geoMatchName: 'Tennessee', displayOrder: 43 },
	{ id: '48', scopeCode: 'US', name: 'Texas', geoMatchName: 'Texas', displayOrder: 44 },
	{ id: '49', scopeCode: 'US', name: 'Utah', geoMatchName: 'Utah', displayOrder: 45 },
	{ id: '50', scopeCode: 'US', name: 'Vermont', geoMatchName: 'Vermont', displayOrder: 46 },
	{ id: '51', scopeCode: 'US', name: 'Virginia', geoMatchName: 'Virginia', displayOrder: 47 },
	{ id: '53', scopeCode: 'US', name: 'Washington', geoMatchName: 'Washington', displayOrder: 48 },
	{ id: '54', scopeCode: 'US', name: 'West Virginia', geoMatchName: 'West Virginia', displayOrder: 49 },
	{ id: '55', scopeCode: 'US', name: 'Wisconsin', geoMatchName: 'Wisconsin', displayOrder: 50 },
	{ id: '56', scopeCode: 'US', name: 'Wyoming', geoMatchName: 'Wyoming', displayOrder: 51 },
	{ id: '57', scopeCode: 'CAN', name: 'Alberta', geoMatchName: 'Alberta', displayOrder: 1 },
	{ id: '58', scopeCode: 'CAN', name: 'British Columbia', geoMatchName: 'British Columbia', displayOrder: 2 },
	{ id: '59', scopeCode: 'CAN', name: 'Manitoba', geoMatchName: 'Manitoba', displayOrder: 3 },
	{ id: '60', scopeCode: 'CAN', name: 'New Brunswick', geoMatchName: 'New Brunswick', displayOrder: 4 },
	{ id: '61', scopeCode: 'CAN', name: 'Newfoundland and Labrador', geoMatchName: 'Newfoundland and Labrador', displayOrder: 5 },
	{ id: '62', scopeCode: 'CAN', name: 'Northwest Territories', geoMatchName: 'Northwest Territories', displayOrder: 6 },
	{ id: '63', scopeCode: 'CAN', name: 'Nova Scotia', geoMatchName: 'Nova Scotia', displayOrder: 7 },
	{ id: '64', scopeCode: 'CAN', name: 'Nunavut', geoMatchName: 'Nunavut', displayOrder: 8 },
	{ id: '65', scopeCode: 'CAN', name: 'Ontario', geoMatchName: 'Ontario', displayOrder: 9 },
	{ id: '66', scopeCode: 'CAN', name: 'Prince Edward Island', geoMatchName: 'Prince Edward Island', displayOrder: 10 },
	{ id: '67', scopeCode: 'CAN', name: 'Quebec', geoMatchName: 'Quebec', displayOrder: 11 },
	{ id: '68', scopeCode: 'CAN', name: 'Saskatchewan', geoMatchName: 'Saskatchewan', displayOrder: 12 },
	{ id: '69', scopeCode: 'CAN', name: 'Yukon', geoMatchName: 'Yukon', displayOrder: 13 },
	{ id: '70', scopeCode: 'MX', name: 'Aguascalientes', geoMatchName: 'Aguascalientes', displayOrder: 1 },
	{ id: '71', scopeCode: 'MX', name: 'Baja California', geoMatchName: 'Baja California', displayOrder: 2 },
	{ id: '72', scopeCode: 'MX', name: 'Baja California Sur', geoMatchName: 'Baja California Sur', displayOrder: 3 },
	{ id: '73', scopeCode: 'MX', name: 'Campeche', geoMatchName: 'Campeche', displayOrder: 4 },
	{ id: '74', scopeCode: 'MX', name: 'Chiapas', geoMatchName: 'Chiapas', displayOrder: 5 },
	{ id: '75', scopeCode: 'MX', name: 'Chihuahua', geoMatchName: 'Chihuahua', displayOrder: 6 },
	{ id: '76', scopeCode: 'MX', name: 'Coahuila de Zaragoza', geoMatchName: 'Coahuila de Zaragoza', displayOrder: 7 },
	{ id: '77', scopeCode: 'MX', name: 'Colima', geoMatchName: 'Colima', displayOrder: 8 },
	{ id: '78', scopeCode: 'MX', name: 'Durango', geoMatchName: 'Durango', displayOrder: 9 },
	{ id: '79', scopeCode: 'MX', name: 'Guanajuato', geoMatchName: 'Guanajuato', displayOrder: 10 },
	{ id: '80', scopeCode: 'MX', name: 'Guerrero', geoMatchName: 'Guerrero', displayOrder: 11 },
	{ id: '81', scopeCode: 'MX', name: 'Hidalgo', geoMatchName: 'Hidalgo', displayOrder: 12 },
	{ id: '82', scopeCode: 'MX', name: 'Jalisco', geoMatchName: 'Jalisco', displayOrder: 13 },
	{ id: '83', scopeCode: 'MX', name: 'México', geoMatchName: 'México', displayOrder: 14 },
	{ id: '84', scopeCode: 'MX', name: 'Distrito Federal', geoMatchName: 'Distrito Federal', displayOrder: 15 },
	{ id: '85', scopeCode: 'MX', name: 'Michoacán de Ocampo', geoMatchName: 'Michoacán de Ocampo', displayOrder: 16 },
	{ id: '86', scopeCode: 'MX', name: 'Morelos', geoMatchName: 'Morelos', displayOrder: 17 },
	{ id: '87', scopeCode: 'MX', name: 'Nayarit', geoMatchName: 'Nayarit', displayOrder: 18 },
	{ id: '88', scopeCode: 'MX', name: 'Nuevo León', geoMatchName: 'Nuevo León', displayOrder: 19 },
	{ id: '89', scopeCode: 'MX', name: 'Oaxaca', geoMatchName: 'Oaxaca', displayOrder: 20 },
	{ id: '90', scopeCode: 'MX', name: 'Puebla', geoMatchName: 'Puebla', displayOrder: 21 },
	{ id: '91', scopeCode: 'MX', name: 'Querétaro', geoMatchName: 'Querétaro', displayOrder: 22 },
	{ id: '92', scopeCode: 'MX', name: 'Quintana Roo', geoMatchName: 'Quintana Roo', displayOrder: 23 },
	{ id: '93', scopeCode: 'MX', name: 'San Luis Potosí', geoMatchName: 'San Luis Potosí', displayOrder: 24 },
	{ id: '94', scopeCode: 'MX', name: 'Sinaloa', geoMatchName: 'Sinaloa', displayOrder: 25 },
	{ id: '95', scopeCode: 'MX', name: 'Sonora', geoMatchName: 'Sonora', displayOrder: 26 },
	{ id: '96', scopeCode: 'MX', name: 'Tabasco', geoMatchName: 'Tabasco', displayOrder: 27 },
	{ id: '97', scopeCode: 'MX', name: 'Tamaulipas', geoMatchName: 'Tamaulipas', displayOrder: 28 },
	{ id: '98', scopeCode: 'MX', name: 'Tlaxcala', geoMatchName: 'Tlaxcala', displayOrder: 29 },
	{ id: '99', scopeCode: 'MX', name: 'Veracruz de Ignacio de la Llave', geoMatchName: 'Veracruz de Ignacio de la Llave', displayOrder: 30 },
	{ id: '100', scopeCode: 'MX', name: 'Yucatán', geoMatchName: 'Yucatán', displayOrder: 31 },
	{ id: '101', scopeCode: 'MX', name: 'Zacatecas', geoMatchName: 'Zacatecas', displayOrder: 32 },
];

/** @type {{ scopes: typeof FALLBACK_SCOPES, jurisdictions: typeof FALLBACK_JURISDICTIONS } | null} */
let cachedCatalog = null;
/** @type {Promise<{ scopes: any[], jurisdictions: any[] }> | null} */
let inflight = null;

export const FALLBACK_CATALOG = {
	scopes: FALLBACK_SCOPES,
	jurisdictions: FALLBACK_JURISDICTIONS,
};

/**
 * @param {string} [apiBaseUrl]
 * @param {{ force?: boolean }} [opts]
 */
export async function fetchJurisdictionsCatalog(apiBaseUrl = DEFAULT_API_BASE, opts = {}) {
	if (cachedCatalog && !opts.force) return cachedCatalog;
	if (inflight && !opts.force) return inflight;

	inflight = fetch(`${apiBaseUrl}/jurisdictions`)
		.then(async (res) => {
			if (!res.ok) throw new Error(`jurisdictions catalog HTTP ${res.status}`);
			const data = await res.json();
			const scopes = Array.isArray(data.scopes) ? data.scopes : [];
			const jurisdictions = Array.isArray(data.jurisdictions) ? data.jurisdictions : [];
			if (!scopes.length || !jurisdictions.length) {
				throw new Error('jurisdictions catalog empty');
			}
			cachedCatalog = { scopes, jurisdictions };
			return cachedCatalog;
		})
		.catch((err) => {
			console.warn('[PlateChase] jurisdictions catalog fetch failed; using fallback', err);
			if (!cachedCatalog) cachedCatalog = FALLBACK_CATALOG;
			return cachedCatalog;
		})
		.finally(() => {
			inflight = null;
		});

	return inflight;
}

export function getCachedCatalog() {
	return cachedCatalog;
}

export function clearJurisdictionsCatalogCache() {
	cachedCatalog = null;
	inflight = null;
}

/**
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null | undefined} catalog
 */
export function effectiveCatalog(catalog) {
	if (catalog?.scopes?.length && catalog?.jurisdictions?.length) return catalog;
	return cachedCatalog || FALLBACK_CATALOG;
}

/**
 * Toggle/list rows for a map type. COMBINED uses US rows (same as legacy toggle list).
 * @param {string} code
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 * @returns {{ id: string, name: string }[]}
 */
export function jurisdictionsForScope(code, catalog) {
	const { jurisdictions } = effectiveCatalog(catalog);
	const scopeCode = code === 'COMBINED' || !code ? 'US' : code;
	return jurisdictions
		.filter((j) => j.scopeCode === scopeCode)
		.slice()
		.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || String(a.name).localeCompare(String(b.name)))
		.map((j) => ({ id: j.id, name: j.name }));
}

/**
 * @param {string} id
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 */
export function nameById(id, catalog) {
	if (id == null || id === '') return '';
	const { jurisdictions } = effectiveCatalog(catalog);
	const row = jurisdictions.find((j) => j.id === id);
	return row ? row.name : '';
}

/** Same id→name data as the toggle list; use for modals / reports instead of duplicating hashes. */
export function regionDisplayNameById(id, catalog) {
	const name = nameById(id, catalog);
	return name || (id == null || id === '' ? '' : String(id));
}

/**
 * GeoJSON feature name → jurisdiction id maps for CAN / MX (and US when names are present).
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 * @returns {{ CAN: Record<string, string>, MX: Record<string, string>, US: Record<string, string> }}
 */
export function geoMatchMaps(catalog) {
	const { jurisdictions } = effectiveCatalog(catalog);
	const maps = { US: {}, CAN: {}, MX: {} };
	let hasCan = false;
	let hasMx = false;
	for (const j of jurisdictions) {
		if (!j.geoMatchName || !maps[j.scopeCode]) continue;
		maps[j.scopeCode][j.geoMatchName] = j.id;
		if (j.scopeCode === 'CAN') hasCan = true;
		if (j.scopeCode === 'MX') hasMx = true;
	}
	if (!hasCan) Object.assign(maps.CAN, CANADIAN_PROVINCES);
	if (!hasMx) Object.assign(maps.MX, MEXICAN_STATES);
	return maps;
}

/**
 * Scope options shaped like STAT_SCOPE_OPTIONS for CountryToggler / ReportsFilterBar.
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 */
export function scopeOptionsFromCatalog(catalog) {
	const { scopes } = effectiveCatalog(catalog);
	if (!scopes?.length) return STAT_SCOPE_OPTIONS;
	return scopes
		.slice()
		.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
		.map((s) => ({
			code: s.code,
			name: s.label || s.name || s.code,
			flag: s.flag || '',
			regionsLabel: s.regionsLabel || `${s.label || s.code} regions`,
		}));
}

/**
 * @param {string} code
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 */
export function getScopeByCode(code, catalog) {
	const { scopes } = effectiveCatalog(catalog);
	return scopes.find((s) => s.code === code) || scopes[0] || FALLBACK_SCOPES[0];
}

/**
 * Denominator counts from catalog jurisdiction rows.
 * @param {{ scopes?: any[], jurisdictions?: any[] } | null} [catalog]
 * @returns {Record<'US' | 'CAN' | 'MX' | 'COMBINED', number>}
 */
export function scopeDenominatorsFromCatalog(catalog) {
	const { jurisdictions } = effectiveCatalog(catalog);
	const counts = { US: 0, CAN: 0, MX: 0, COMBINED: 0 };
	for (const j of jurisdictions) {
		if (j.scopeCode === 'US' || j.scopeCode === 'CAN' || j.scopeCode === 'MX') {
			counts[j.scopeCode] += 1;
			counts.COMBINED += 1;
		}
	}
	return counts;
}

/**
 * Split catalog projection payload into react-simple-maps projection name + config.
 * @param {object | null | undefined} projectionPayload
 */
export function splitProjection(projectionPayload) {
	if (!projectionPayload || typeof projectionPayload !== 'object') {
		return { projection: 'geoAlbersUsa', projectionConfig: undefined };
	}
	const { projection, ...rest } = projectionPayload;
	const projectionConfig = Object.keys(rest).length ? rest : undefined;
	return {
		projection: projection || 'geoAlbersUsa',
		projectionConfig,
	};
}

export const JurisdictionsCatalogContext = createContext({
	catalog: null,
	loaded: false,
	scopeOptions: STAT_SCOPE_OPTIONS,
	denominators: null,
	refreshCatalog: async () => null,
});

export function useJurisdictionsCatalog() {
	return useContext(JurisdictionsCatalogContext);
}

/**
 * @param {{ catalog: any, loaded: boolean, refreshCatalog: () => Promise<any>, children: React.ReactNode }} props
 */
export function JurisdictionsCatalogProvider({ catalog, loaded, refreshCatalog, children }) {
	const value = useMemo(() => {
		const effective = effectiveCatalog(catalog);
		return {
			catalog: effective,
			loaded: Boolean(loaded && catalog),
			scopeOptions: scopeOptionsFromCatalog(effective),
			denominators: scopeDenominatorsFromCatalog(effective),
			refreshCatalog,
		};
	}, [catalog, loaded, refreshCatalog]);

	return (
		<JurisdictionsCatalogContext.Provider value={value}>{children}</JurisdictionsCatalogContext.Provider>
	);
}
